import api from "./axios";
import { AxiosResponse } from "axios";

export type MarketPriceMode = "COUNT_WISE" | "PER_KG";

interface BackendResponse<T> {
  data: T;
  message: string;
  success: boolean;
  timestamp: string;
}

export interface MarketPriceArea {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MarketPriceCommodity {
  id: string;
  name: string;
  defaultPricingMode: MarketPriceMode;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MarketPriceRow {
  id: string;
  areaId: string;
  commodityId: string;
  pricingMode: MarketPriceMode;
  count: string | null;
  label: string | null;
  pricePerKg: number;
  isActive: boolean;
  isPublic?: boolean;
  createdAt: string;
  updatedAt: string;
  area?: MarketPriceArea;
  commodity?: MarketPriceCommodity;
}

export interface CreateMarketPriceAreaDto {
  name: string;
  isActive?: boolean;
}

export interface UpdateMarketPriceAreaDto {
  name?: string;
  isActive?: boolean;
}

export interface CreateMarketPriceCommodityDto {
  name: string;
  defaultPricingMode: MarketPriceMode;
  isActive?: boolean;
}

export interface UpdateMarketPriceCommodityDto {
  name?: string;
  defaultPricingMode?: MarketPriceMode;
  isActive?: boolean;
}

export interface CreateMarketPriceRowDto {
  areaId: string;
  commodityId: string;
  pricingMode: MarketPriceMode;
  count?: string;
  label?: string;
  pricePerKg: number;
  isActive?: boolean;
  isPublic?: boolean;
}

export interface UpdateMarketPriceRowDto {
  areaId?: string;
  commodityId?: string;
  pricingMode?: MarketPriceMode;
  count?: string;
  label?: string;
  pricePerKg?: number;
  isActive?: boolean;
  isPublic?: boolean;
}

const unwrap = <T>(response: AxiosResponse<BackendResponse<T> | T>): T => {
  const payload = response.data as BackendResponse<T>;
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }
  return response.data as T;
};

export const fetchMarketPriceAreas = async (activeOnly = true): Promise<MarketPriceArea[]> => {
  const response = await api.get<BackendResponse<MarketPriceArea[]> | MarketPriceArea[]>("/market-prices/areas", {params: {activeOnly}});
  return unwrap(response);
};

export const createMarketPriceArea = async (dto: CreateMarketPriceAreaDto): Promise<MarketPriceArea> => {
  const response = await api.post<BackendResponse<MarketPriceArea> | MarketPriceArea>("/market-prices/areas", dto);
  return unwrap(response);
};

export const updateMarketPriceArea = async (id: string, dto: UpdateMarketPriceAreaDto): Promise<MarketPriceArea> => {
  const response = await api.patch<BackendResponse<MarketPriceArea> | MarketPriceArea>(`/market-prices/areas/${id}`, dto);
  return unwrap(response);
};

export const deleteMarketPriceArea = async (id: string): Promise<void> => {
  await api.delete(`/market-prices/areas/${id}`);
};

export const fetchMarketPriceCommodities = async (activeOnly = true): Promise<MarketPriceCommodity[]> => {
  const response = await api.get<BackendResponse<MarketPriceCommodity[]> | MarketPriceCommodity[]>("/market-prices/commodities", {
    params: {activeOnly},
  });
  return unwrap(response);
};

export const createMarketPriceCommodity = async (dto: CreateMarketPriceCommodityDto): Promise<MarketPriceCommodity> => {
  const response = await api.post<BackendResponse<MarketPriceCommodity> | MarketPriceCommodity>("/market-prices/commodities", dto);
  return unwrap(response);
};

export const updateMarketPriceCommodity = async (id: string, dto: UpdateMarketPriceCommodityDto): Promise<MarketPriceCommodity> => {
  const response = await api.patch<BackendResponse<MarketPriceCommodity> | MarketPriceCommodity>(`/market-prices/commodities/${id}`, dto);
  return unwrap(response);
};

export const deleteMarketPriceCommodity = async (id: string): Promise<void> => {
  await api.delete(`/market-prices/commodities/${id}`);
};

export const fetchMarketPrices = async (params: {areaId?: string; commodityId?: string; activeOnly?: boolean}): Promise<MarketPriceRow[]> => {
  const response = await api.get<BackendResponse<MarketPriceRow[]> | MarketPriceRow[]>("/market-prices", {params});
  return unwrap(response);
};

export const fetchPublicMarketPrices = async (limit = 3): Promise<MarketPriceRow[]> => {
  const response = await api.get<BackendResponse<MarketPriceRow[]> | MarketPriceRow[]>("/market-prices/public", {params: {limit}});
  return unwrap(response);
};

export const createMarketPriceRow = async (dto: CreateMarketPriceRowDto): Promise<MarketPriceRow> => {
  const response = await api.post<BackendResponse<MarketPriceRow> | MarketPriceRow>("/market-prices", dto);
  return unwrap(response);
};

export const updateMarketPriceRow = async (id: string, dto: UpdateMarketPriceRowDto): Promise<MarketPriceRow> => {
  const response = await api.patch<BackendResponse<MarketPriceRow> | MarketPriceRow>(`/market-prices/${id}`, dto);
  return unwrap(response);
};

export const deleteMarketPriceRow = async (id: string): Promise<void> => {
  await api.delete(`/market-prices/${id}`);
};
